import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Package, CreditCard, Printer, ChevronRight, FileText, Copy, Check } from 'lucide-react';
import { VENDOR_TEMPLATE_SHORT, VENDOR_TEMPLATE_FULL } from '../lib/vendorProfileTemplate';

export default function VendorHelpPage() {
  const handlePrint = () => window.print();
  const [copiedKey, setCopiedKey] = useState(null);

  const copyTemplate = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Template copied to clipboard');
      setTimeout(() => setCopiedKey(null), 2500);
    } catch (_e) {
      toast.error('Copy failed — select the text and copy manually');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-12 py-12 w-full">
        {/* Print-only banner */}
        <div className="hidden print:block mb-6 text-center">
          <h1 className="text-2xl font-bold">Christ Kingdom Global Marketplace</h1>
          <p className="text-sm text-muted-foreground">Vendor Onboarding Guide</p>
          <p className="text-xs text-muted-foreground mt-1">christkingdomglobalmarketplace.com</p>
        </div>

        <div className="flex items-start justify-between gap-3 mb-8 flex-wrap print:hidden">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight" data-testid="vendor-help-heading">
              Vendor Help Center
            </h1>
            <p className="text-muted-foreground mt-2">
              Everything you need to start selling on Christ Kingdom Global Marketplace.
            </p>
          </div>
          <Button onClick={handlePrint} variant="outline" className="gap-2" data-testid="download-pdf-btn">
            <Printer className="h-4 w-4" />
            Download as PDF
          </Button>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10 print:hidden">
          <a href="#upload-products" className="block p-4 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Guide 1: Upload a Product</p>
                  <p className="text-xs text-muted-foreground">10 min</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </a>
          <a href="#stripe-setup" className="block p-4 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Guide 2: Connect Stripe</p>
                  <p className="text-xs text-muted-foreground">5 min · CRITICAL</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </a>
          <a href="#profile-template" className="block p-4 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors" data-testid="quicklink-profile-template">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Guide 3: Profile Template</p>
                  <p className="text-xs text-muted-foreground">Copy-paste starter</p>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </a>
        </div>

        {/* GUIDE 1 */}
        <section id="upload-products" className="scroll-mt-20 mb-12 print:break-before-page">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-3 text-2xl">
                <Package className="h-6 w-6 text-primary" />
                Guide 1: How to Upload Your First Product
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Time required: 10 minutes per product</p>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none pt-6 space-y-6">
              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 1 — Log into Your Vendor Dashboard</h3>
                <ol className="list-decimal pl-6 space-y-1 text-sm">
                  <li>Go to <strong>christkingdomglobalmarketplace.com</strong></li>
                  <li>Click <strong>&quot;Sign In&quot;</strong> (top right)</li>
                  <li>Sign in with the email you registered with</li>
                  <li>Click <strong>&quot;Vendor Dashboard&quot;</strong> in the top menu</li>
                </ol>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 2 — Navigate to Products</h3>
                <ol className="list-decimal pl-6 space-y-1 text-sm">
                  <li>In your Vendor Dashboard, click the <strong>&quot;Products&quot;</strong> tab</li>
                  <li>Click the orange <strong>&quot;Add Product&quot;</strong> button (top right)</li>
                </ol>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 3 — Fill In Product Details</h3>
                <p className="text-sm text-muted-foreground mb-2">Fill in each field carefully — these are what buyers see:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border">
                    <thead className="bg-muted">
                      <tr>
                        <th className="border border-border px-3 py-2 text-left">Field</th>
                        <th className="border border-border px-3 py-2 text-left">What to Enter</th>
                        <th className="border border-border px-3 py-2 text-left">Tip</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-border px-3 py-2"><strong>Product Name</strong></td>
                        <td className="border border-border px-3 py-2">Clear, searchable name (e.g., &quot;Cross Necklace — Sterling Silver&quot;)</td>
                        <td className="border border-border px-3 py-2">Use words buyers actually search for</td>
                      </tr>
                      <tr>
                        <td className="border border-border px-3 py-2"><strong>Description</strong></td>
                        <td className="border border-border px-3 py-2">3-5 sentences about what it is, materials, meaning</td>
                        <td className="border border-border px-3 py-2">Christians buy on emotion — share the story</td>
                      </tr>
                      <tr>
                        <td className="border border-border px-3 py-2"><strong>Price (USD)</strong></td>
                        <td className="border border-border px-3 py-2">Your retail price (e.g., 29.99)</td>
                        <td className="border border-border px-3 py-2">Round numbers like .99 convert best</td>
                      </tr>
                      <tr>
                        <td className="border border-border px-3 py-2"><strong>Category</strong></td>
                        <td className="border border-border px-3 py-2">Pick the closest match</td>
                        <td className="border border-border px-3 py-2">Helps buyers find you</td>
                      </tr>
                      <tr>
                        <td className="border border-border px-3 py-2"><strong>Stock Quantity</strong></td>
                        <td className="border border-border px-3 py-2">How many you have available</td>
                        <td className="border border-border px-3 py-2">Decreases automatically with each sale</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 4 — Upload Product Photos</h3>
                <ol className="list-decimal pl-6 space-y-1 text-sm">
                  <li>Click <strong>&quot;Upload Images&quot;</strong></li>
                  <li>Upload <strong>3-5 high-quality photos</strong>:
                    <ul className="list-disc pl-6 mt-1">
                      <li>1 main image (clean white background or natural daylight)</li>
                      <li>1 detail/close-up shot</li>
                      <li>1 lifestyle shot (showing the product in use)</li>
                      <li>1 size reference (if applicable)</li>
                    </ul>
                  </li>
                </ol>
                <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-3 text-xs space-y-1">
                  <p className="font-semibold text-amber-900">Image Rules:</p>
                  <p>✅ Minimum 800×800 pixels · Natural daylight beats studio lights · Multiple angles</p>
                  <p>❌ Don&apos;t use stock photos you don&apos;t own — copyright matters</p>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 5 — Click &quot;Save Product&quot;</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>✅ Product is LIVE on the marketplace within seconds</li>
                  <li>✅ It appears under your storefront URL: <code className="bg-muted px-2 py-0.5 rounded text-xs">christkingdomglobalmarketplace.com/store/your-shop-name</code></li>
                  <li>✅ It also appears in the main product catalog when buyers browse</li>
                </ul>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 6 — Share Your Listing</h3>
                <p className="text-sm">After saving, go to your Vendor Dashboard overview → use the <strong>&quot;Share Your Store&quot;</strong> buttons:</p>
                <ul className="list-disc pl-6 space-y-1 text-sm mt-2">
                  <li>📱 WhatsApp (one-click share to your contacts)</li>
                  <li>📘 Facebook · 🐦 Twitter/X · 📧 Email</li>
                  <li>🔲 QR code (print on flyers, church bulletins)</li>
                </ul>
                <p className="text-sm text-primary mt-3 font-medium">
                  Vendors who share their store on the day they list get their first sale within 48 hours.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* GUIDE 2 */}
        <section id="stripe-setup" className="scroll-mt-20 mb-12 print:break-before-page">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-3 text-2xl">
                <CreditCard className="h-6 w-6 text-primary" />
                Guide 2: How to Connect Stripe (So You Can Get Paid)
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Time required: 5 minutes · ⚠️ CRITICAL</p>
            </CardHeader>
            <CardContent className="pt-6 space-y-6 text-sm">
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                <p className="font-semibold text-orange-900 mb-1">Why Stripe is Required</p>
                <p>Without this step, buyers cannot pay you. Sales won&apos;t reach your bank.</p>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Why You Need Stripe</h3>
                <p className="text-sm">Christ Kingdom Marketplace uses <strong>Stripe Connect</strong> to process payments. This means:</p>
                <ul className="list-disc pl-6 space-y-1 text-sm mt-2">
                  <li>✅ Buyers pay via credit/debit card, Apple Pay, Google Pay</li>
                  <li>✅ Stripe deposits your share directly to YOUR bank (not ours)</li>
                  <li>✅ Christ Kingdom takes only a 7-10% platform fee — you keep the rest</li>
                  <li>✅ Stripe handles 1099 tax forms automatically (US vendors)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 1 — Log into Your Vendor Dashboard</h3>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>Sign in at <strong>christkingdomglobalmarketplace.com</strong></li>
                  <li>Click <strong>&quot;Vendor Dashboard&quot;</strong></li>
                  <li>You&apos;ll see an <strong>orange banner</strong>: <em>&quot;Connect Stripe — you can&apos;t receive payments yet&quot;</em></li>
                </ol>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 2 — Click &quot;Connect Stripe Now&quot;</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Click the orange <strong>&quot;Connect Stripe Now&quot;</strong> button in the banner</li>
                  <li>(Or go to <strong>Vendor Dashboard → Earnings → &quot;Connect with Stripe&quot;</strong>)</li>
                  <li>You&apos;ll be redirected to Stripe&apos;s secure onboarding page</li>
                </ul>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 3 — Have These Ready Before Starting</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border">
                    <thead className="bg-muted">
                      <tr>
                        <th className="border border-border px-3 py-2 text-left">Document / Info</th>
                        <th className="border border-border px-3 py-2 text-left">Why Stripe Needs It</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td className="border border-border px-3 py-2">📧 Email address</td><td className="border border-border px-3 py-2">Login + tax form delivery</td></tr>
                      <tr><td className="border border-border px-3 py-2">📞 Mobile phone</td><td className="border border-border px-3 py-2">2FA + account verification</td></tr>
                      <tr><td className="border border-border px-3 py-2">🆔 Government ID (driver&apos;s license / passport)</td><td className="border border-border px-3 py-2">Identity verification</td></tr>
                      <tr><td className="border border-border px-3 py-2">🏢 Business name (or your legal name)</td><td className="border border-border px-3 py-2">Tax + bank deposit name</td></tr>
                      <tr><td className="border border-border px-3 py-2">📍 Business address</td><td className="border border-border px-3 py-2">Required for tax forms</td></tr>
                      <tr><td className="border border-border px-3 py-2">🏦 Bank routing + account number</td><td className="border border-border px-3 py-2">Where your payouts go</td></tr>
                      <tr><td className="border border-border px-3 py-2">🔢 SSN or EIN (US vendors)</td><td className="border border-border px-3 py-2">Tax reporting / 1099-K</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 4 — Complete Each Stripe Screen</h3>
                <ol className="list-decimal pl-6 space-y-1.5">
                  <li><strong>Account Type</strong> — Choose &quot;Individual&quot; if sole proprietor or &quot;Company&quot; if LLC/Corp</li>
                  <li><strong>Business Details</strong> — Name, website (use your storefront URL), industry</li>
                  <li><strong>Personal Info</strong> — Legal name, date of birth, address (must match ID)</li>
                  <li><strong>Identity Verification</strong> — Upload your government ID. Verifies in ~2 min</li>
                  <li><strong>Bank Account</strong> — Routing + account number (or instant login for Chase/BofA/Wells)</li>
                  <li><strong>Tax Info</strong> — SSN or EIN (US) / local tax ID (international)</li>
                  <li><strong>Review &amp; Submit</strong> — Double-check → Click <strong>&quot;Submit&quot;</strong></li>
                </ol>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">Step 5 — Wait for Approval</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>⏰ Usually <strong>instant</strong> for US vendors with clean info</li>
                  <li>⏰ Can take <strong>24-48 hours</strong> if Stripe needs additional documents</li>
                  <li>📧 You&apos;ll get an email when approved</li>
                </ul>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg mb-2">💰 How Money Flows (Example: $49.99 Sale)</h3>
                <pre className="bg-muted/60 border border-border rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono">{`1. Buyer pays $49.99 at checkout
2. Stripe instantly splits the payment:
   • Christ Kingdom platform fee: ~$3.50-$5.00 → Datasamllc
   • Stripe processing fee: ~$1.75 (from your share)
   • Your net: ~$42-45 → into YOUR Stripe balance
3. Stripe deposits to your bank:
   • First 7-10 days: held for risk protection
   • After 30 days clean: 2-day rolling payouts (automatic)`}</pre>
              </div>

              <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded p-4">
                <p className="font-semibold text-amber-900 mb-1">🌟 Founding Vendor Bonus</p>
                <p className="text-sm text-amber-800">If you&apos;re one of the first <strong>100 vendors</strong>, you get <strong>lifetime 7% platform fee</strong> (instead of 10%) — that&apos;s an extra $1.50 per $50 sale, every sale, forever.</p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* GUIDE 3 — Profile Template */}
        <section id="profile-template" className="scroll-mt-20 mb-12 print:break-before-page">
          <Card data-testid="vendor-profile-template-section">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-3 text-2xl">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                Vendor Profile Template
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                A faith-honoring, copy-paste framework for your storefront profile. Two lengths — pick the one that fits.
              </p>
            </CardHeader>
            <CardContent className="pt-6 space-y-8">
              {/* Short version */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h3 className="font-heading font-semibold text-lg">Short version — for your storefront bio</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyTemplate('short', VENDOR_TEMPLATE_SHORT)}
                    data-testid="copy-template-short"
                  >
                    {copiedKey === 'short' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    {copiedKey === 'short' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-2">~60 words. Perfect for the short bio field on your storefront card.</p>
                <pre className="bg-muted/50 border border-border rounded-md p-4 text-sm whitespace-pre-wrap font-sans leading-relaxed">
{VENDOR_TEMPLATE_SHORT}
                </pre>
              </div>

              {/* Full version */}
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h3 className="font-heading font-semibold text-lg">Full version — for your About / Story page</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyTemplate('full', VENDOR_TEMPLATE_FULL)}
                    data-testid="copy-template-full"
                  >
                    {copiedKey === 'full' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    {copiedKey === 'full' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-2">~250 words. Five sections: Mission, Story, How We Serve, Kingdom Impact, Connect.</p>
                <pre className="bg-muted/50 border border-border rounded-md p-4 text-sm whitespace-pre-wrap font-sans leading-relaxed">
{VENDOR_TEMPLATE_FULL}
                </pre>
              </div>

              <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded p-4 text-sm">
                <p className="font-semibold mb-1">✍️ How to use these</p>
                <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
                  <li>Click <strong>Copy</strong> on whichever version fits.</li>
                  <li>Paste into your storefront profile / About page.</li>
                  <li>Replace <strong>every [bracketed]</strong> placeholder with your real specifics — the more concrete you get, the more buyers trust you.</li>
                  <li>Do <strong>not</strong> leave any placeholder unfilled — an empty <code>[Founder Name]</code> looks worse than no template at all.</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Help footer */}
        <Card className="bg-primary/5 border-primary/20 print:break-before-page">
          <CardContent className="p-6 text-center">
            <h3 className="font-heading font-semibold text-lg mb-2">Need Help?</h3>
            <p className="text-sm text-muted-foreground mb-3">
              📧 Email: <a href="mailto:admin@christkingdomglobalmarketplace.com" className="text-primary underline">admin@christkingdomglobalmarketplace.com</a>
            </p>
            <p className="text-sm text-muted-foreground mb-3">
              Reply to any Christ Kingdom email — a real human responds.
            </p>
            <div className="flex justify-center gap-3 flex-wrap print:hidden">
              <Link to="/vendor/covenant"><Button variant="outline" size="sm">Partnership Covenant</Button></Link>
              <Link to="/terms"><Button variant="outline" size="sm">Terms of Service</Button></Link>
              <Link to="/vendor"><Button size="sm">Open Vendor Dashboard</Button></Link>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />

      {/* Print-only CSS — hides header/footer/buttons when saving as PDF */}
      <style>{`
        @media print {
          header, footer, nav, .print\\:hidden { display: none !important; }
          body { background: white; }
          .print\\:break-before-page { break-before: page; }
          .print\\:block { display: block !important; }
          a { color: #000; text-decoration: none; }
          .text-primary { color: #C45D35 !important; }
        }
      `}</style>
    </div>
  );
}
